<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingDateRule',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      required: false,
    },
    labelColsMd: {
      type: null,
      required: false,
      default: 3,
    },
    contentColsMd: {
      type: null,
      required: false,
      default: 9,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const offsetDays = ref(null);

  const $refs = {
    get offsetDays() {
      return offsetDays.value;
    },
  };

  const onCheckChanged = (...args) => {
    if (isOffsetDaysSet.value) {
      setData('item.offset_days', 0);
    } else {
      setData('item.offset_days', null);
    }
  };

  const metadata = ref(null);
  const labelAlign = ref('right');
  const isOffsetDaysSet = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    metadata: metadata.value,
    labelAlign: labelAlign.value,
    isOffsetDaysSet: isOffsetDaysSet.value,
    ux: ux.value,
  }));

  const $$load_metadata = async () => {
    return $app.value.repo.get('ui_metadata');
  };
  const $$load_isOffsetDaysSet = async () => {
    return props.item.offset_days != null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'metadata' },
      { name: 'labelAlign' },
      { name: 'isOffsetDaysSet' },
      { name: 'ux' },
    ],
    loaders: {
      metadata: $$load_metadata,
      isOffsetDaysSet: $$load_isOffsetDaysSet,
    },
    ux: {},
    dataRefs: { metadata, labelAlign, isOffsetDaysSet, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const anchorOptions = computed(() => {
    if (loaded.value) {
      return metadata.value.billing_date_rule_anchor_options;
    }
    return null;
  });
  const target = computed(() => {
    return props.item;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.editing,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editing');
    }
  );
  watch(
    () => props.labelColsMd,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelColsMd');
    }
  );
  watch(
    () => props.contentColsMd,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contentColsMd');
    }
  );

  const act_2140198529698299_script_2lvwhpw19_value_callable = async (
    $event
  ) => {
    return nextTick(() => onCheckChanged(isOffsetDaysSet.value));
  };
  const act_2140198529698299_script_2lvwhpw19_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2140198529698299_script_2lvwhpw19_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_7wk5l5vqp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_p2axa2jzq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7wk5l5vqp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_p2axa2jzq],
    event: 'reload',
  });
  const act_2140198529698299_script_2lvwhpw19 = new actionTypes.ScriptAction({
    actionArgs: act_2140198529698299_script_2lvwhpw19_getActionArgs,
  });
  const inst_evh_2140198529698299_change_fe1n8tseb =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_script_2lvwhpw19,
      event: 'change',
      displayName: 'script',
    });
  const inst_evh_2140198529698299_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_change_fe1n8tseb],
    event: 'change',
  });

  const actions = {
    inst_evh_7315092382398562_reload_p2axa2jzq,
    inst_evh_7315092382398562_reload,
    inst_evh_2140198529698299_change_fe1n8tseb,
    inst_evh_2140198529698299_change,
  };

  const evh_7315092382398562_reload_p2axa2jzq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_p2axa2jzq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_change_fe1n8tseb = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_change_fe1n8tseb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_change = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :labelColsMd="labelColsMd"
      :contentColsMd="contentColsMd"
      name="description"
      :label="pgettext('BillingDateRule', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="description"
        v-model="item.description"
        :value="item.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :labelColsMd="labelColsMd"
      :contentColsMd="contentColsMd"
      name="anchor"
      :label="pgettext('BillingDateRule', 'Anchor date')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="anchor"
        v-model="item.anchor"
        :value="item.anchor"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.anchor =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="anchorOptions"
        valueField="value"
        textField="name"
        required
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :labelColsMd="labelColsMd"
      :contentColsMd="contentColsMd"
      name="offset_days"
      :label="pgettext('BillingDateRule', 'Offset')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <div class="input-group-text">
        <input
          type="checkbox"
          v-model="isOffsetDaysSet"
          :disabled="!editing"
          @change="evh_2140198529698299_change($event, {})"
        />
      </div>
      <ItpFormInput
        type="number"
        name="offset_days"
        v-model="item.offset_days"
        :value="item.offset_days"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.offset_days =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :disabled="!isOffsetDaysSet"
        ref="offsetDays"
      >
      </ItpFormInput>
      <span class="input-group-text">
        {{ pgettext('InputGroupAddon', 'day') }}
      </span>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
